import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Clock, User, ArrowLeft, Minus, Plus, Check, Ticket } from "lucide-react";
import { toast } from "sonner";
import { fetchEvent, createReservation } from "@/lib/api";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchEvent(slug)
      .then(setEvent)
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const reserve = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      const r = await createReservation({
        event_id: event.id,
        name: form.name,
        email: form.email,
        quantity: form.quantity,
      });
      setConfirmed(r);
      toast.success(`Confirmed · Code ${r.confirmation_code}`);
      // refresh event seat count
      const updated = await fetchEvent(slug);
      setEvent(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reservation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="event-detail-loading" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="h-[60vh] bg-white/[0.04] animate-pulse" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="font-display text-4xl uppercase">Event not found.</p>
        <Link to="/events" className="mt-6 inline-block text-[#B4FF39] underline">Browse all events</Link>
      </div>
    );
  }

  const remaining = event.capacity - event.seats_taken;

  return (
    <div data-testid="event-detail-page">
      {/* Hero Image */}
      <div className="relative w-full h-[55vh] md:h-[70vh] overflow-hidden">
        <img src={event.image_url} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-transparent to-[#0A0A0A]" />
        <div className="absolute top-6 left-6 md:top-10 md:left-12">
          <Link
            to="/events"
            data-testid="back-to-events"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-[#0A0A0A]/60 backdrop-blur-md text-xs uppercase tracking-[0.2em] font-bold hover:border-[#B4FF39] hover:text-[#B4FF39]"
          >
            <ArrowLeft className="w-3 h-3" /> All Events
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-24 md:-mt-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main content */}
          <div className="lg:col-span-8 space-y-10">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#B4FF39] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold">
                  {event.category}
                </span>
                <span className="px-3 py-1 bg-[#121212]/80 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.2em] font-bold border border-white/10">
                  {event.city}
                </span>
              </div>
              <h1
                data-testid="event-title"
                className="font-display text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.92]"
              >
                {event.title}
              </h1>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                <Cell icon={Calendar} label="Date" value={formatDate(event.date)} />
                <Cell icon={Clock} label="Doors" value={event.time} />
                <Cell icon={MapPin} label="Venue" value={event.venue} />
                <Cell icon={User} label="By" value={event.organizer} />
              </div>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">◆ About</p>
              <p className="text-base md:text-lg text-zinc-300 leading-[1.8] whitespace-pre-line">
                {event.description}
              </p>
            </div>

            <div className="border-t border-white/5 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="From" value={`$${event.price_from.toFixed(0)}`} />
              <Stat label="Capacity" value={event.capacity.toLocaleString()} />
              <Stat label="Seats Left" value={remaining > 0 ? remaining.toLocaleString() : "Sold Out"} highlight={remaining <= 0} />
              <Stat label="Doors" value={event.time} />
            </div>
          </div>

          {/* Sticky RSVP */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-[#121212] border border-white/10 p-6 md:p-8">
              {confirmed ? (
                <div data-testid="reservation-confirmation" className="space-y-5 text-center">
                  <div className="w-14 h-14 mx-auto bg-[#B4FF39] flex items-center justify-center">
                    <Check className="w-7 h-7 text-[#0A0A0A]" strokeWidth={3} />
                  </div>
                  <p className="font-display text-2xl uppercase">You're In.</p>
                  <p className="text-sm text-zinc-400">
                    Confirmation sent to <span className="text-white">{confirmed.email}</span>
                  </p>
                  <div className="border border-white/10 p-4 bg-[#0A0A0A]">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Code</p>
                    <p data-testid="confirmation-code" className="font-mono-tight text-2xl text-[#B4FF39] tracking-[0.2em]">
                      {confirmed.confirmation_code}
                    </p>
                  </div>
                  <p className="text-xs text-zinc-500">
                    Flash this code at the door. {confirmed.quantity} ticket{confirmed.quantity > 1 ? "s" : ""}.
                  </p>
                  <Link
                    to="/my-reservations"
                    className="mt-2 inline-flex items-center justify-center gap-2 w-full bg-white text-[#0A0A0A] font-bold py-3 hover:bg-zinc-200 uppercase tracking-[0.15em] text-xs"
                  >
                    <Ticket className="w-4 h-4" /> View My Tickets
                  </Link>
                </div>
              ) : remaining <= 0 ? (
                <div className="text-center space-y-3">
                  <p className="font-display text-3xl uppercase text-[#B4FF39]">Sold Out</p>
                  <p className="text-sm text-zinc-400">Join the waitlist to be notified if seats open up.</p>
                </div>
              ) : (
                <form data-testid="rsvp-form" onSubmit={reserve} className="space-y-5">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Reserve Your Spot</p>
                    <p className="font-display text-3xl mt-2 uppercase tracking-tight">${event.price_from.toFixed(0)}<span className="text-sm text-zinc-500 ml-1">from</span></p>
                  </div>

                  <Field label="Name">
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="rsvp-name-input"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#B4FF39] outline-none font-mono-tight text-sm placeholder:text-zinc-600"
                      placeholder="Full name"
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="rsvp-email-input"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#B4FF39] outline-none font-mono-tight text-sm placeholder:text-zinc-600"
                      placeholder="you@email.com"
                    />
                  </Field>

                  <Field label="Tickets">
                    <div className="flex items-center border border-white/10 bg-[#0A0A0A]">
                      <button
                        type="button"
                        data-testid="qty-decrement"
                        onClick={() => setForm({ ...form, quantity: Math.max(1, form.quantity - 1) })}
                        className="w-12 h-12 flex items-center justify-center hover:bg-white/5"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span data-testid="qty-value" className="flex-1 text-center font-mono-tight text-lg">{form.quantity}</span>
                      <button
                        type="button"
                        data-testid="qty-increment"
                        onClick={() => setForm({ ...form, quantity: Math.min(remaining, Math.min(10, form.quantity + 1)) })}
                        className="w-12 h-12 flex items-center justify-center hover:bg-white/5"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </Field>

                  <div className="border-t border-white/5 pt-4 flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Total estimate</span>
                    <span className="font-display text-xl">${(event.price_from * form.quantity).toFixed(0)}</span>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="rsvp-submit-button"
                    className="w-full bg-[#B4FF39] text-[#0A0A0A] font-bold py-4 hover:bg-[#9FE822] transition-colors uppercase tracking-[0.15em] text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? "Reserving…" : "Confirm Reservation"}
                  </button>

                  <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-center">
                    No payment required · MVP RSVP
                  </p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Cell({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#121212] p-5">
      <div className="flex items-center gap-2 mb-2 text-[#B4FF39]">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{label}</p>
      </div>
      <p className="text-sm font-medium leading-snug">{value}</p>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</p>
      <p className={`font-display text-2xl uppercase ${highlight ? "text-[#B4FF39]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{label}</span>
      {children}
    </label>
  );
}
